/**
 * Old TJR job files (`Report_Log_*.tjf`) as history for Insights (R249, decision 1049; docs/specs/cloud/insights.md
 * §8). The file is read in the person's browser by `readTjrContainer`, the reader of "Import aircraft" and "Import
 * staff"; the fields below are the ones Teslyn Field's `.tjf` import reads (crates/teslyn-core/src/tjf/import.rs:
 * `daily`, `line_statuses`, `project`; field layouts in `TJF_IMPORT_EXPORT_SPEC.md` §3).
 *
 * A history job is not a project. It keeps only what the production figures need: dates, hours, fuel litres, standby
 * types, pilot names, aircraft and line km. Nothing else is read from the file, so TJR's rates, charges, contract
 * text, comments and phone numbers never come across (decision 1048: no money figures).
 *
 * `planHistoryImport` decides what an import may add: a job already in Teslyn, as a project or as history, is shown as
 * a duplicate and is never overwritten (`history_job_add` refuses it in the database too).
 */
import { isoDate, parseTjrDate } from './dates.js';
import type { Acceptance, EntryType, IsoDate, StandbyType } from './model.js';
import { opsSummary, type OpsEntry, type OpsLine } from './ops.js';
import { normalisePersonName, personNameKey } from './staff.js';
import { TjrFileError, readTjrContainer, tjrTypeName, type TjrRecord } from './tjr-defaults.js';

/** The only `.tjf` version Teslyn reads (import.rs `TJF_VERSION`). */
export const TJF_VERSION = 59;

/** TJR stores metres to 0.01; a line short of its flown length by more than this is part accepted (import.rs). */
const METRE_TOLERANCE = 0.05;

/** One daily entry of a history job: the columns the Hub's rules read, with the pilot's name and the registration. */
export interface HistoryEntry {
  /** The entry's own index in the file. */
  readonly index: number;
  readonly date: IsoDate;
  readonly rego: string | null;
  readonly pilot_name: string | null;
  readonly entry_type: EntryType;
  readonly standby_type: StandbyType;
  readonly days_alloc: number;
  readonly svy_hours: number;
  readonly cbx_hours: number;
  readonly cal_hours: number;
  readonly fry_hours: number;
  readonly ext_hours: number;
  readonly fuel_used_l: number;
  readonly mobilization: boolean;
  readonly flt_num: number | null;
}

/** One line record of a history job, with its entry's day, flight and aircraft. */
export interface HistoryLine {
  readonly date: IsoDate;
  readonly flt_num: number | null;
  readonly rego: string | null;
  readonly line_number: number;
  readonly acceptance: Acceptance;
  readonly use_km: boolean;
  readonly flown_m: number;
  readonly accepted_m: number;
  readonly reason: string;
}

/** What `history_job.data` holds: production figures only. */
export interface HistoryJobData {
  readonly aircraft: readonly { readonly registration: string; readonly type_name: string }[];
  readonly blocks: readonly { readonly name: string; readonly line_spacing_m: number | null }[];
  readonly entries: readonly HistoryEntry[];
  readonly lines: readonly HistoryLine[];
}

export interface HistoryJob {
  /** "F26044", as Teslyn Field's import numbers the job (letter, five digits, suffix). */
  readonly number: string;
  readonly title: string;
  readonly client_name: string;
  readonly file_name: string;
  readonly first_date: IsoDate | null;
  readonly last_date: IsoDate | null;
  readonly data: HistoryJobData;
}

/** A `history_job` row as Insights reads it. */
export interface HistoryJobRow extends HistoryJob {
  readonly id: string;
  readonly imported_at: string;
  readonly imported_by_name: string;
}

const scalar = (records: readonly TjrRecord[], code: string): string =>
  records.find((r) => r.code === code)?.fields[0] ?? '';

const fieldsOf = (records: readonly TjrRecord[], code: string): (readonly string[])[] =>
  records.filter((r) => r.code === code).map((r) => r.fields);

function numberOf(f: readonly string[], i: number, what: string): number {
  const t = (f[i] ?? '').trim();
  const n = t === '' ? Number.NaN : Number(t);
  if (!Number.isFinite(n)) throw new TjrFileError(`This project file has a ${what} that is not a number.`);
  return n;
}

/** Every rule that reads a line number needs a whole number (`parseLineNumber`). */
function lineNumberOf(f: readonly string[]): number {
  const n = numberOf(f, 2, 'line number');
  if (!Number.isInteger(n) || n < 0)
    throw new TjrFileError('This project file has a line number that is not valid.');
  return n;
}

const optionalNumber = (s: string | undefined): number | null => {
  const t = (s ?? '').trim();
  const n = t === '' ? Number.NaN : Number(t);
  return Number.isFinite(n) ? n : null;
};

/** List rows carry their own index in field 0 (F26048 stores its pilots as 2, 1, 0), so place them by it. */
function byIndex(
  rows: readonly (readonly string[])[],
  value: (f: readonly string[]) => string,
): Map<number, string> {
  const out = new Map<number, string>();
  for (const f of rows) {
    const i = optionalNumber(f[0]);
    if (i !== null && !out.has(i)) out.set(i, value(f));
  }
  return out;
}

/** "F26048": letter from the job type (008), digits (021) to five places, suffix (023), as Teslyn Field's import. */
function jobNumber(records: readonly TjrRecord[]): string {
  const letter = scalar(records, '008').trim() === '1' ? 'H' : 'F';
  const digits = scalar(records, '021').trim();
  if (digits === '') throw new TjrFileError('This project file has no job number.');
  const n = /^\d+$/.test(digits) ? digits.padStart(5, '0') : digits;
  return `${letter}${n}${scalar(records, '023').trim()}`;
}

const ENTRY_TYPES: Readonly<Record<string, EntryType>> = {
  '1': 'flight',
  '2': 'standby',
  '3': 'maintenance',
  '4': 'suspended',
};

const STANDBY_TYPES: Readonly<Record<string, StandbyType>> = {
  '0': 'diurnal',
  '1': 'weather',
  '2': 'pilot_rdo',
  '3': 'other',
};

/** Overview §5, as Teslyn Field's import: accepted km give accepted or partial; none with a reason is rejected. */
function acceptanceOf(flownM: number, acceptedM: number, reason: string): Acceptance {
  if (acceptedM > 0) return acceptedM < flownM - METRE_TOLERANCE ? 'partial' : 'accepted';
  return reason.trim() !== '' ? 'rejected' : 'pending';
}

function dateOf(f: readonly string[]): IsoDate {
  try {
    return isoDate(parseTjrDate((f[1] ?? '').trim()));
  } catch {
    throw new TjrFileError('This project file has a daily entry with no proper date.');
  }
}

/** A TJR job file as a history job. `TjrFileError` says in words why a file cannot be read. */
export function readTjrHistoryJob(bytes: Uint8Array, fileName: string): HistoryJob {
  const records = readTjrContainer(bytes);
  if (!records.some((r) => r.code === '013')) {
    throw new TjrFileError('This file is not a project file (Report_Log_*.tjf).');
  }
  const version = optionalNumber(scalar(records, '005'));
  if (version !== TJF_VERSION) {
    throw new TjrFileError(
      `This project file is version ${version ?? 'unknown'}; Teslyn reads version ${TJF_VERSION} only.`,
    );
  }

  const pilots = byIndex(fieldsOf(records, '010'), (f) => normalisePersonName(f[1] ?? ''));
  const aircraftRows = fieldsOf(records, '012');
  const regos = byIndex(aircraftRows, (f) => (f[1] ?? '').trim().toUpperCase());

  const entries: HistoryEntry[] = fieldsOf(records, '100').map((f) => {
    const entry_type = ENTRY_TYPES[(f[12] ?? '').trim()];
    if (!entry_type) throw new TjrFileError('This project file has a daily entry of an unknown kind.');
    const flt = numberOf(f, 11, 'flight number');
    return {
      index: numberOf(f, 0, 'daily entry number'),
      date: dateOf(f),
      rego: regos.get(numberOf(f, 7, 'aircraft number')) || null,
      pilot_name: pilots.get(numberOf(f, 10, 'pilot number')) || null,
      entry_type,
      standby_type: STANDBY_TYPES[(f[31] ?? '').trim()] ?? null,
      days_alloc: numberOf(f, 13, 'day count'),
      svy_hours: numberOf(f, 14, 'survey hours figure'),
      cbx_hours: numberOf(f, 15, 'comp box hours figure'),
      cal_hours: numberOf(f, 16, 'calibration hours figure'),
      fry_hours: numberOf(f, 17, 'ferry hours figure'),
      ext_hours: numberOf(f, 18, 'external hours figure'),
      fuel_used_l: numberOf(f, 19, 'fuel figure'),
      mobilization: (f[35] ?? '').trim() === '-1',
      flt_num: flt > 0 ? flt : null,
    };
  });

  // a line record names its daily entry by position in the file (import.rs `line_statuses`)
  const lines: HistoryLine[] = fieldsOf(records, '400').map((f) => {
    const entry = entries[numberOf(f, 1, 'daily entry number')];
    if (!entry) throw new TjrFileError('This project file has a line for a daily entry it does not hold.');
    const flown_m = numberOf(f, 5, 'flown length');
    const accepted_m = numberOf(f, 6, 'accepted length');
    const reason = f[7] ?? '';
    return {
      date: entry.date,
      flt_num: entry.flt_num,
      rego: entry.rego,
      line_number: lineNumberOf(f),
      acceptance: acceptanceOf(flown_m, accepted_m, reason),
      use_km: (f[4] ?? '').trim() === '-1',
      flown_m,
      accepted_m,
      reason,
    };
  });

  const dates = entries.map((e) => e.date).sort();
  const aircraft = aircraftRows.flatMap((f) => {
    const registration = (f[1] ?? '').trim().toUpperCase();
    if (!registration) return [];
    return [
      { registration, type_name: tjrTypeName({ brand: (f[9] ?? '').trim(), model: (f[10] ?? '').trim() }) },
    ];
  });
  const blocks = fieldsOf(records, '013').flatMap((f) => {
    const name = (f[0] ?? '').trim();
    return name ? [{ name, line_spacing_m: optionalNumber(f[4]) }] : [];
  });
  return {
    number: jobNumber(records),
    title: scalar(records, '020').trim(),
    client_name: scalar(records, '022').trim(),
    file_name: fileName,
    first_date: dates[0] ?? null,
    last_date: dates[dates.length - 1] ?? null,
    data: { aircraft, blocks, entries, lines },
  };
}

/** A history job's rows in the shape of the Hub's rules; `aircraft_id` is the registration. */
export function historyOpsRows(
  jobId: string,
  data: HistoryJobData,
): { readonly lines: readonly OpsLine[]; readonly entries: readonly OpsEntry[] } {
  const entries: OpsEntry[] = data.entries.map((e) => ({
    id: `${jobId}:e${e.index}`,
    project_id: jobId,
    date: e.date,
    aircraft_id: e.rego,
    entry_type: e.entry_type,
    standby_type: e.standby_type,
    days_alloc: e.days_alloc,
    svy_hours: e.svy_hours,
    cbx_hours: e.cbx_hours,
    cal_hours: e.cal_hours,
    fry_hours: e.fry_hours,
    ext_hours: e.ext_hours,
    fuel_used_l: e.fuel_used_l,
    mobilization: e.mobilization,
    flt_num: e.flt_num,
    op_comment: '',
  }));
  const lines: OpsLine[] = data.lines.map((l) => ({
    project_id: jobId,
    flight_id: `${jobId}:${l.date}:${l.flt_num ?? 0}`,
    flight_date: l.date,
    aircraft_id: l.rego,
    rego: l.rego,
    line_number: l.line_number,
    acceptance: l.acceptance,
    use_km: l.use_km,
    flown_m: l.flown_m,
    // TJR keeps one length: the import's proposed length is the flown length, so km flown is TJR's own
    proposed_m: l.flown_m,
    accepted_m: l.accepted_m,
    log_completion: null,
  }));
  return { lines, entries };
}

/** What the import's preview shows of a job. */
export interface HistoryPreview {
  readonly flownKm: number;
  readonly acceptedKm: number;
  readonly flightHours: number;
  readonly surveyHours: number;
  readonly standbyDays: number;
  readonly pilots: readonly string[];
}

export function historyPreview(job: HistoryJob): HistoryPreview {
  const { lines, entries } = historyOpsRows('preview', job.data);
  const s = opsSummary(lines, entries, {
    from: job.first_date ?? '0000-01-01',
    to: job.last_date ?? '9999-12-31',
    projectId: null,
    aircraftId: null,
  });
  const names = new Map<string, string>();
  for (const e of job.data.entries) {
    if (e.pilot_name && e.entry_type === 'flight') names.set(personNameKey(e.pilot_name), e.pilot_name);
  }
  return {
    flownKm: s.flownKm,
    acceptedKm: s.acceptedKm,
    flightHours: s.flightHours,
    surveyHours: s.surveyHours,
    standbyDays: s.standby.total,
    pilots: [...names.values()].sort((a, b) => a.localeCompare(b)),
  };
}

/** The key a job number is compared by: "f26048 " and "F26048" are one job (as `history_job_add`). */
export const jobNumberKey = (number: string): string => number.trim().toUpperCase();

export type HistoryImportStatus = 'new' | 'in_teslyn' | 'already_history' | 'repeated' | 'empty';

const HISTORY_NOTE: Readonly<Record<HistoryImportStatus, string>> = {
  new: '',
  in_teslyn: 'Already a project in Teslyn, so it is left as it is.',
  already_history: 'Already imported. Remove it first to import this file instead.',
  repeated: 'Another file of the same job. The one with the latest last date is used.',
  empty: 'This file holds no daily entries.',
};

export interface HistoryImportRow {
  readonly job: HistoryJob;
  readonly preview: HistoryPreview;
  readonly status: HistoryImportStatus;
  /** Why the job is not added, in words; '' for a new job. */
  readonly note: string;
  /** Ticked by default: every new job. Anything else cannot be ticked. */
  readonly selected: boolean;
}

/**
 * What importing these jobs would do, one row per file. A job already in Teslyn is a duplicate whatever the file holds:
 * nothing of it is changed. Of two files of one job (TJR's weekly copies), the one with the latest last date is kept.
 */
export function planHistoryImport(
  jobs: readonly HistoryJob[],
  projectNumbers: readonly string[],
  historyNumbers: readonly string[],
): HistoryImportRow[] {
  const projects = new Set(projectNumbers.map(jobNumberKey));
  const history = new Set(historyNumbers.map(jobNumberKey));
  const newest = new Map<string, HistoryJob>();
  for (const j of jobs) {
    const k = jobNumberKey(j.number);
    const had = newest.get(k);
    if (!had || (j.last_date ?? '') > (had.last_date ?? '')) newest.set(k, j);
  }
  return jobs.map((job) => {
    const k = jobNumberKey(job.number);
    const status: HistoryImportStatus = projects.has(k)
      ? 'in_teslyn'
      : history.has(k)
        ? 'already_history'
        : newest.get(k) !== job
          ? 'repeated'
          : job.data.entries.length === 0
            ? 'empty'
            : 'new';
    return {
      job,
      preview: historyPreview(job),
      status,
      note: HISTORY_NOTE[status],
      selected: status === 'new',
    };
  });
}
